"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
// TODO: Import the server action for creating a tenant
// import { createTenantAction } from "../../../app/actions/tenantActions"; // No longer needed
import { apiRequest } from "@/lib/queryClient"; // Import apiRequest

interface CreateTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTenantCreated: () => void; // Callback to refresh data
}

export default function CreateTenantModal({
  isOpen,
  onClose,
  onTenantCreated,
}: CreateTenantModalProps) {
  const { toast } = useToast();
  const [tenantName, setTenantName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsCreating(true);

    if (!tenantName.trim()) {
      toast({
        title: "Error",
        description: "Tenant name cannot be empty.",
        variant: "destructive",
      });
      setIsCreating(false);
      return;
    }

    try {
      // TODO: Call the server action to create the tenant
      // await createTenantAction({ name: tenantName }); 
      // await createTenantAction({ name: tenantName.trim() }); // Old server action call
      await apiRequest("POST", "/api/tenants", { name: tenantName.trim() });

      toast({
        title: "Success",
        description: `Tenant "${tenantName}" created successfully.`,
      });
      onTenantCreated(); // Refresh data on the parent page
      onClose(); // Close the modal
      setTenantName(""); // Reset tenant name
    } catch (error) {
      console.error("Failed to create tenant:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create tenant. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Tenant</DialogTitle>
          <DialogDescription>
            Enter the name for the new tenant.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="tenantName" className="text-right">
                Tenant Name
              </Label>
              <Input
                id="tenantName"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                className="col-span-3"
                placeholder="Enter tenant name"
                disabled={isCreating}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? "Creating..." : "Create Tenant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
} 